import LegalLayout from "@/components/legal/LegalLayout";
import { useI18n } from "@/lib/i18n";
import { CONTACT_EMAIL } from "@/data/landing";

export default function Terms() {
  const { t, lang } = useI18n();
  return (
    <LegalLayout
      title={t("terms.title")}
      kicker={t("terms.kicker")}
      lastUpdated={t("terms.lastUpdated")}
    >
      {lang === "tr" ? <TrContent /> : <EnContent />}
    </LegalLayout>
  );
}

function EnContent() {
  return (
    <>
      <p>
        Welcome to Health Break. These Terms of Use (&ldquo;Terms&rdquo;) govern your use
        of the Health Break mobile application and any related services (together, the
        &ldquo;Service&rdquo;). By creating an account or using the Service, you agree to
        these Terms.
      </p>

      <h2>1. Who can use Health Break</h2>
      <p>
        You must be at least 13 years old (or the minimum age required in your country)
        to use Health Break. By using the Service you confirm that you meet this
        requirement.
      </p>

      <h2>2. Your account</h2>
      <p>
        You can create an account by signing in with Apple, Google, or with email and
        password. Keep your sign-in details safe — you&rsquo;re responsible for activity
        on your account. Tell us right away at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you suspect
        unauthorised access.
      </p>

      <h2>3. Health &amp; safety disclaimer</h2>
      <p>
        Health Break offers gentle, beginner-friendly micro-exercises designed for desk
        workers. <strong>It is not medical advice.</strong> The Service is not a
        substitute for professional medical care, diagnosis, or treatment. Always:
      </p>
      <ul>
        <li>Listen to your body and stop if anything feels painful.</li>
        <li>Consult a qualified healthcare professional before starting if you have a medical condition, injury, or are pregnant.</li>
        <li>Skip any movement that doesn&rsquo;t feel right for your body.</li>
      </ul>
      <p>You use the exercises at your own discretion and risk.</p>

      <h2>4. Subscriptions &amp; payments</h2>
      <p>
        The core Health Break experience is free. Optional Pro features may be offered as
        an in-app subscription billed through the App Store or Google Play. Subscriptions
        renew automatically unless cancelled at least 24 hours before the end of the
        current period. You can manage or cancel from your store account settings at any
        time.
      </p>

      <h2>5. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Reverse engineer, decompile or attempt to extract the source code of the app.</li>
        <li>Use the Service to harass, abuse or harm others.</li>
        <li>Resell, sublicense or commercially exploit any part of the Service without our written consent.</li>
        <li>Probe, scan or test the vulnerability of any part of the Service.</li>
      </ul>

      <h2>6. Intellectual property</h2>
      <p>
        Health Break, including its design, logo, copy, illustrations and content, is
        owned by us and protected by intellectual property laws. We grant you a limited,
        personal, non-transferable licence to use the app for your own wellness — that&rsquo;s it.
      </p>

      <h2>7. User content</h2>
      <p>
        You may log how a session felt, add notes or set custom preferences. You retain
        ownership of that content. By using the Service you grant us a limited licence to
        store and display that content back to you within the app.
      </p>

      <h2>8. Termination</h2>
      <p>
        You can stop using Health Break and delete your account at any time from{" "}
        <em>Settings → Account → Delete account</em>. We may suspend or terminate your
        access if you breach these Terms or use the Service in a way that puts others at
        risk.
      </p>

      <h2>9. Disclaimer of warranties</h2>
      <p>
        Health Break is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;.
        While we work hard to keep the Service stable and useful, we don&rsquo;t promise
        it will be uninterrupted, error-free, or fit for any specific purpose beyond what
        is described in our marketing materials.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, Health Break is not liable for indirect,
        incidental or consequential damages arising from your use of the Service. Our
        total liability is limited to the amount you have paid us in the 12 months prior
        to the event giving rise to the claim.
      </p>

      <h2>11. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. If a change is material, we&rsquo;ll
        notify you in-app or by email before it takes effect. Continued use of the
        Service after a change means you accept the updated Terms.
      </p>

      <h2>12. Contact</h2>
      <p>
        Questions about these Terms? Email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </>
  );
}

function TrContent() {
  return (
    <>
      <p>
        Health Break&rsquo;e hoş geldiniz. Bu Kullanım Koşulları
        (&ldquo;Koşullar&rdquo;), Health Break mobil uygulamasını ve ilgili hizmetleri
        (birlikte &ldquo;Hizmet&rdquo;) kullanmanızı düzenler. Bir hesap oluşturarak veya
        Hizmet&rsquo;i kullanarak bu Koşulları kabul etmiş olursunuz.
      </p>

      <h2>1. Health Break&rsquo;i kim kullanabilir</h2>
      <p>
        Health Break&rsquo;i kullanmak için en az 13 yaşında olmanız (ya da ülkenizde
        gereken asgari yaşı sağlamanız) gerekir. Hizmet&rsquo;i kullanarak bu şartı
        karşıladığınızı beyan etmiş olursunuz.
      </p>

      <h2>2. Hesabınız</h2>
      <p>
        Apple, Google veya e-posta + şifre ile giriş yaparak hesap oluşturabilirsiniz.
        Giriş bilgilerinizi güvende tutun — hesabınızdaki etkinliklerden siz
        sorumlusunuz. İzinsiz erişimden şüpheleniyorsanız hemen{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> adresine yazın.
      </p>

      <h2>3. Sağlık ve güvenlik uyarısı</h2>
      <p>
        Health Break, masa başı çalışanlar için tasarlanmış nazik ve yeni başlayan dostu
        mikro-egzersizler sunar. <strong>Tıbbi tavsiye değildir.</strong> Hizmet,
        profesyonel tıbbi bakım, teşhis veya tedavinin yerini tutmaz. Her zaman:
      </p>
      <ul>
        <li>Bedeninizi dinleyin, ağrı hissederseniz durun.</li>
        <li>Bir tıbbi durumunuz, yaralanmanız varsa ya da hamileyseniz başlamadan önce nitelikli bir sağlık uzmanına danışın.</li>
        <li>Bedeninize uygun gelmeyen herhangi bir hareketi atlayın.</li>
      </ul>
      <p>Egzersizleri kendi takdir ve sorumluluğunuzla yaparsınız.</p>

      <h2>4. Abonelikler ve ödemeler</h2>
      <p>
        Health Break&rsquo;in çekirdek deneyimi ücretsizdir. Opsiyonel Pro özellikler
        App Store veya Google Play üzerinden faturalanan uygulama içi abonelik olarak
        sunulabilir. Abonelikler, mevcut dönem sona ermeden en az 24 saat önce iptal
        edilmediği sürece otomatik olarak yenilenir. İstediğiniz zaman mağaza hesabı
        ayarlarınızdan yönetebilir veya iptal edebilirsiniz.
      </p>

      <h2>5. Kabul edilebilir kullanım</h2>
      <p>Şunları yapmamayı kabul edersiniz:</p>
      <ul>
        <li>Uygulamanın kaynak kodunu tersine mühendislik yapmak, derlemeyi açmak veya çıkarmaya çalışmak.</li>
        <li>Hizmet&rsquo;i başkalarını taciz etmek, istismar etmek veya zarar vermek için kullanmak.</li>
        <li>Yazılı iznimiz olmadan Hizmet&rsquo;in herhangi bir bölümünü yeniden satmak, alt lisanslamak veya ticari olarak istismar etmek.</li>
        <li>Hizmet&rsquo;in herhangi bir bölümünün güvenlik açıklarını araştırmak, taramak veya test etmek.</li>
      </ul>

      <h2>6. Fikri mülkiyet</h2>
      <p>
        Health Break, tasarımı, logosu, metinleri, illüstrasyonları ve içeriği dahil, bize
        aittir ve fikri mülkiyet yasalarıyla korunur. Size yalnızca uygulamayı kendi
        wellness&rsquo;iniz için kullanmanızı sağlayan sınırlı, kişisel ve devredilemez
        bir lisans veririz — başka bir şey değil.
      </p>

      <h2>7. Kullanıcı içeriği</h2>
      <p>
        Bir seansın nasıl hissettirdiğini kaydedebilir, notlar ekleyebilir veya özel
        tercihler belirleyebilirsiniz. Bu içeriğin sahipliği size aittir. Hizmet&rsquo;i
        kullanarak bu içeriği uygulama içinde size geri göstermek üzere depolamak için
        bize sınırlı bir lisans verirsiniz.
      </p>

      <h2>8. Hesabın sona ermesi</h2>
      <p>
        Health Break&rsquo;i kullanmayı bırakabilir ve hesabınızı istediğiniz zaman{" "}
        <em>Ayarlar → Hesap → Hesabı sil</em> menüsünden silebilirsiniz. Bu Koşulları ihlal
        etmeniz veya başkalarını riske atan bir biçimde Hizmet&rsquo;i kullanmanız
        durumunda erişiminizi askıya alabilir veya sonlandırabiliriz.
      </p>

      <h2>9. Garanti reddi</h2>
      <p>
        Health Break, &ldquo;olduğu gibi&rdquo; ve &ldquo;mevcut olduğu şekilde&rdquo;
        sunulur. Hizmet&rsquo;i istikrarlı ve faydalı tutmak için çok çalışsak da,
        kesintisiz, hatasız veya pazarlama materyallerimizde tarif edilenin ötesinde
        belirli bir amaca uygun olacağını taahhüt etmiyoruz.
      </p>

      <h2>10. Sorumluluğun sınırlandırılması</h2>
      <p>
        Yasaların izin verdiği azami ölçüde, Health Break Hizmet&rsquo;i kullanmanızdan
        kaynaklanan dolaylı, arızi veya sonuç olarak ortaya çıkan zararlardan sorumlu
        değildir. Toplam sorumluluğumuz, talebe yol açan olaydan önceki 12 ayda bize
        ödediğiniz tutarla sınırlıdır.
      </p>

      <h2>11. Bu Koşullardaki değişiklikler</h2>
      <p>
        Bu Koşulları zaman zaman güncelleyebiliriz. Önemli bir değişiklik olduğunda
        yürürlüğe girmeden önce uygulama içinde veya e-posta ile bilgilendiririz.
        Değişikliğin ardından Hizmet&rsquo;i kullanmaya devam etmeniz, güncellenmiş
        Koşulları kabul ettiğiniz anlamına gelir.
      </p>

      <h2>12. İletişim</h2>
      <p>
        Bu Koşullarla ilgili sorularınız mı var?{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> adresine yazın.
      </p>
    </>
  );
}
